<template>
  <article class="live-sale-card" :class="{ 'live-sale-card--live': live }">
    <header class="live-sale-card__head">
      <LiveBadge v-if="live" class="live-sale-card__live" :label="liveLabel" />
      <button type="button" class="live-sale-card__name" @click="emit('open')">{{ name }}</button>
    </header>

    <div class="live-sale-card__meta">
      <span class="live-sale-card__type" :class="`live-sale-card__type--${saleType}`">
        <i class="pi live-sale-card__type-icon" :class="`pi-${typeIcon}`" />{{ typeLabel }}
      </span>
      <span class="live-sale-card__where">{{ where }}</span>
    </div>

    <footer class="live-sale-card__foot">
      <span class="live-sale-card__counts">
        <span class="live-sale-card__run">{{ runCount }}</span>
        <span v-if="crossedCount" class="live-sale-card__crossed">/ {{ crossedCount }}</span>
        {{ runLabel }}
        <template v-if="myListCount">
          <span class="live-sale-card__separator">·</span>
          <span class="live-sale-card__my-list">{{ myListCount }} {{ myListLabel }}</span>
        </template>
      </span>

      <button v-if="joinable" type="button" class="live-sale-card__join" @click="emit('join')">
        <i class="pi pi-play-circle live-sale-card__join-icon" />{{ joinLabel }}
      </button>
    </footer>
  </article>
</template>

<script setup>
import { computed } from 'vue';
import { LiveBadge } from '@hds/auto-auction-com-components-shared';

// One running sale in the LIVE NOW rail.
//
// The card answers the only question a dealer asks of a lane they are not in
// yet: is anything of MINE in it. That is why `myListCount` sits beside the run
// count rather than on a detail screen — "12 / 41 run · 2 on My List" is the
// difference between joining and scrolling past, and it is one glance.
//
// The type chip is the sale's FORMAT, not its state. Simulcast has an
// auctioneer and a floor; automated runs itself; timed has no lane at all, only
// a clock. A dealer bids differently in each, so the format is on the card
// rather than discovered after joining.
//
// Every figure arrives pre-formatted or as a plain count. The card does no
// arithmetic — see the run counts, which the rail's own header sums.
defineOptions({ name: 'LiveSaleCard' });

const props = defineProps({
    name: { type: String, required: true },
    // simulcast | automated | timed — the sale's format. Unknown values fall
    // back to simulcast rather than rendering an empty chip.
    saleType: {
        type: String,
        default: 'simulcast',
        validator: (value) => ['simulcast', 'automated', 'timed'].includes(value),
    },
    // "Fleet/Lease · Auburn, WA" — the seller channel and where the sale is,
    // joined by the caller because which half exists varies by sale.
    where: { type: String, default: '' },
    // Assets in the lane. `crossedCount` is the sale's whole run list where the
    // lane is only part of it — rendered as "12 / 41 run".
    runCount: { type: Number, default: 0 },
    crossedCount: { type: Number, default: 0 },
    // How many of those the dealer already tracks. Zero hides the clause.
    myListCount: { type: Number, default: 0 },
    live: { type: Boolean, default: true },
    liveLabel: { type: String, default: 'LIVE' },
    runLabel: { type: String, default: 'run' },
    myListLabel: { type: String, default: 'on My List' },
    joinLabel: { type: String, default: 'Join' },
    // False hides Join, for a lane the viewer has no way into. The name still
    // emits `open`; what that does is the caller's business.
    joinable: { type: Boolean, default: true },
});

const emit = defineEmits(['join', 'open']);

// The format's word and its glyph, together, so a chip is never colour alone —
// the same reason the mobile dashboard's state table carries an icon per row.
// `automated` reads SEQUENTIAL, and the mismatch is deliberate: the event
// runtime's word for the format is AUTOMATED and props carrying the runtime's
// value keep it, but what a dealer reads is Sequential. See
// knowledge-base/glossary.md — a display-label mapping, not a synonym.
const TYPES = {
    simulcast: { label: 'SIMULCAST', icon: 'megaphone' },
    automated: { label: 'SEQUENTIAL', icon: 'bolt' },
    timed: { label: 'TIMED', icon: 'clock' },
};

const type = computed(() => TYPES[props.saleType] ?? TYPES.simulcast);
const typeLabel = computed(() => type.value.label);
const typeIcon = computed(() => type.value.icon);
</script>

<style scoped>
.live-sale-card {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 240px;
  flex: 0 0 auto;
  gap: var(--p-live-sale-card-gap);
  padding: var(--p-live-sale-card-padding);
  border: var(--p-live-sale-card-border-width) solid var(--p-live-sale-card-border-color);
  border-radius: var(--p-live-sale-card-border-radius);
  background: var(--p-live-sale-card-background);
  color: var(--p-live-sale-card-color);
}

/* A live lane earns a left edge, not a filled card. The rail holds seven of
   them and seven red cards is an alarm rather than an index. */
.live-sale-card--live {
  border-left: var(--p-live-sale-card-live-border-left-width) solid var(--p-live-sale-card-live-border-left-color);
}

.live-sale-card__head {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: var(--p-live-sale-card-head-gap);
}



.live-sale-card__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: var(--p-live-sale-card-name-padding);
  color: var(--p-live-sale-card-name-color);
  font-family: inherit;
  font-size: var(--p-live-sale-card-name-font-size);
  font-weight: var(--p-live-sale-card-name-font-weight);
  letter-spacing: var(--p-live-sale-card-name-letter-spacing);
}

.live-sale-card__meta {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: var(--p-live-sale-card-meta-gap);
}

.live-sale-card__type {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  text-transform: uppercase;
  gap: var(--p-live-sale-card-type-gap);
  padding: var(--p-live-sale-card-type-padding);
  border-radius: var(--p-live-sale-card-type-border-radius);
  font-size: var(--p-live-sale-card-type-font-size);
  font-weight: var(--p-live-sale-card-type-font-weight);
  letter-spacing: var(--p-live-sale-card-type-letter-spacing);
}

.live-sale-card__type-icon {
  font-size: var(--p-live-sale-card-type-icon-font-size);
  line-height: 1;
}

.live-sale-card__type--simulcast {
  background: var(--p-live-sale-card-type-simulcast-background);
  color: var(--p-live-sale-card-type-simulcast-color);
}

.live-sale-card__type--automated {
  background: var(--p-live-sale-card-type-automated-background);
  color: var(--p-live-sale-card-type-automated-color);
}

.live-sale-card__type--timed {
  background: var(--p-live-sale-card-type-timed-background);
  color: var(--p-live-sale-card-type-timed-color);
}

.live-sale-card__where {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--p-live-sale-card-where-color);
  font-size: var(--p-live-sale-card-where-font-size);
}

.live-sale-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--p-live-sale-card-foot-gap);
}

.live-sale-card__counts {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--p-live-sale-card-counts-color);
  font-size: var(--p-live-sale-card-counts-font-size);
}

.live-sale-card__run {
  font-family: var(--p-live-sale-card-run-font-family);
  font-weight: var(--p-live-sale-card-run-font-weight);
  color: var(--p-live-sale-card-run-color);
}

.live-sale-card__crossed {
  font-family: var(--p-live-sale-card-crossed-font-family);
  color: var(--p-live-sale-card-crossed-color);
}

.live-sale-card__separator {
  color: var(--p-live-sale-card-separator-color);
}

/* The clause that decides whether the lane is worth joining, so it carries the
   accent rather than sitting in the same grey as the run count. */
.live-sale-card__my-list {
  color: var(--p-live-sale-card-my-list-color);
  font-weight: var(--p-live-sale-card-my-list-font-weight);
}

.live-sale-card__join {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  border: none;
  cursor: pointer;
  gap: var(--p-live-sale-card-join-gap);
  padding: var(--p-live-sale-card-join-padding);
  border-radius: var(--p-live-sale-card-join-border-radius);
  background: var(--p-live-sale-card-join-background);
  color: var(--p-live-sale-card-join-color);
  font-family: inherit;
  font-size: var(--p-live-sale-card-join-font-size);
  font-weight: var(--p-live-sale-card-join-font-weight);
}

.live-sale-card__join:hover {
  background: var(--p-live-sale-card-join-hover-background);
}

.live-sale-card__join-icon {
  font-size: var(--p-live-sale-card-join-icon-font-size);
  line-height: 1;
}

/* Each component that pulses carries its own copy under its own name — a
   library component cannot count on a keyframe declared in an app stylesheet. */
@keyframes live-sale-card-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
</style>
